import { useEffect, useRef, useState } from 'react';

import Search from '@mui/icons-material/Search';
import { CircularProgress, InputAdornment, TextField } from '@mui/material';

/** El silencio que tiene que haber para que lo escrito se convierta en una búsqueda. */
const ESPERA = 300;

interface Props {
  /** Lo que la dirección dice que se está buscando ahora. */
  valor: string;

  /** Si lo último que se pidió todavía está viajando. */
  buscando?: boolean;

  /** Se llama con lo escrito, una sola vez, cuando quedó quieto. */
  onBuscar: (texto: string) => void;
}

/**
 * El campo de búsqueda del catálogo.
 *
 * Vive aparte de la pantalla por dos razones, que son el mismo problema visto de los dos lados.
 *
 * La primera es que lo escrito le pertenece a este campo y a nadie más. La dirección va siempre
 * atrás —cambia recién cuando pasaron los 300ms de silencio y el servidor contestó, medio segundo
 * después— y antes la pantalla copiaba la dirección de vuelta al campo apenas llegaba. Quien
 * escribía "CADENA 428" de corrido veía desaparecer el " 428": la respuesta de "CADENA" aterrizaba
 * y pisaba lo que se había escrito mientras tanto, y encima se llevaba puesta la búsqueda pendiente
 * de esas letras, así que no volvían ni tarde. Acá eso no puede pasar: lo que vuelve de la
 * dirección se compara con lo último que este campo pidió, y si es eso, es el eco de su propio
 * pedido y se ignora.
 *
 * La segunda es que tipear no tiene por qué redibujar el catálogo entero. Con el texto en la
 * pantalla, cada letra volvía a dibujar las doce tarjetas con sus fotos, sus precios y sus
 * animaciones: 63 milisegundos por tecla medidos sobre el sitio publicado, contra 18 en la misma
 * pantalla sin resultados. Escribir rápido era escribirle encima al dibujado. Acá adentro, una
 * letra redibuja un campo de texto.
 */
export function CampoDeBusqueda({ valor, buscando = false, onBuscar }: Readonly<Props>) {
  const [texto, setTexto] = useState(valor);

  /**
   * Lo último que este campo le pidió a la dirección.
   *
   * Es lo que separa "la dirección cambió porque yo se lo pedí" —el eco, que no hay que atender—
   * de "la dirección cambió por otra cosa": alguien borró el chip de la búsqueda, volvió atrás con
   * el navegador, entró con una dirección que ya traía una búsqueda puesta. Eso sí manda.
   */
  const pedido = useRef(valor);

  /**
   * El aviso de ahora.
   *
   * Pasa por una referencia para que el reloj de abajo dependa sólo del texto. Si dependiera de la
   * función —que la pantalla vuelve a crear en cada dibujado— cada respuesta que aterriza
   * reiniciaría la espera, y la búsqueda se iría corriendo sola hacia adelante mientras alguien
   * escribe.
   */
  const avisar = useRef(onBuscar);
  useEffect(() => {
    avisar.current = onBuscar;
  });

  useEffect(() => {
    if (valor === pedido.current) return;

    pedido.current = valor;
    setTexto(valor);
  }, [valor]);

  useEffect(() => {
    if (texto === pedido.current) return;

    const reloj = setTimeout(() => {
      pedido.current = texto;
      avisar.current(texto);
    }, ESPERA);

    return () => clearTimeout(reloj);
  }, [texto]);

  return (
    <TextField
      fullWidth
      label="Buscar por código o descripción"
      variant="outlined"
      value={texto}
      onChange={evento => setTexto(evento.target.value)}
      // Sin esto Chrome ofrece acá lo que la persona escribió alguna vez en cualquier otro
      // formulario —su correo, su nombre— en una lista que cae encima de los filtros de abajo.
      autoComplete="off"
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              {/* La lupa se convierte en reloj mientras la búsqueda viaja. Ocupa el mismo lugar,
                  así que el campo no se mueve, y la espera deja de parecer que no pasó nada. */}
              {buscando ? <CircularProgress size={20} sx={{ m: '2px' }} /> : <Search />}
            </InputAdornment>
          ),
        },
      }}
    />
  );
}
